import { Controller } from "@hotwired/stimulus"
import { syncFilterChip } from "../utils/filter_chip"

// The contact card's one timeline - activity and memories: a search, a row of
// source chips (shared/_timeline_filter) and the "show more"
// reveal. Pure DOM toggling, no fetches — every row the server sent is already
// in the page, so filtering is instant and behaves identically in the sidebar
// and on the profile.
//
// One controller owns BOTH concerns on purpose. They both decide whether a row
// is hidden, and two controllers writing `hidden` on the same elements would
// fight: filtering to Notes would un-hide rows the cap had hidden, and "show
// more" would reveal rows the filter had excluded.
//
// The server renders the initial capped state, so there is no flash of a long
// list before JS runs; this then takes over.
const VISIBLE_CAP = 10

export default class extends Controller {
    static targets = ["moreBtn", "row", "month", "chip", "trigger", "search", "empty", "loadMore", "upcomingMore"]

    connect() {
        this.filter = "all"
        this.expanded = false
        this._apply()
        // Everything after this point is a row arriving from "load older
        // activity" rather than the initial render — see rowTargetConnected.
        this.booted = true
    }

    // Rows appended by the load-older response would otherwise land straight into
    // a collapsed list: the cap doesn't know about them, so it would hide the very
    // rows the press asked for. Pressing it also means "stop capping".
    rowTargetConnected(row) {
        if (!this.booted) return

        // A memory just added (MemoriesController#create) is one row, not a
        // page: re-count, but leave the cap alone.
        if (row.closest("[data-new-memories]")) {
            clearTimeout(this._settle)
            this._settle = setTimeout(() => this._apply(), 0)
            return
        }

        this.expanded = true
        // The append arrives one row at a time; coalesce so a fifty-row page
        // re-applies once instead of fifty times.
        clearTimeout(this._settle)
        this._settle = setTimeout(() => this._apply(), 0)
    }

    // A memory forgotten from its row (memory_grade_controller) takes its row
    // with it; the counts on the chips and the month headings follow.
    rowTargetDisconnected() {
        if (!this.booted) return

        clearTimeout(this._settle)
        this._settle = setTimeout(() => this._apply(), 0)
    }

    // Turbo owns the request; this only says so, since a slow page otherwise looks
    // like a dead button.
    loading(event) {
        event.currentTarget.classList.add("is-loading")
    }

    filterBy(event) {
        const next = event.currentTarget.dataset.source || "all"
        // Tapping the active chip clears it, so the filter is its own undo.
        this.filter = this.filter === next ? "all" : next
        // A filtered list is short by construction; making the user press "show
        // more" again after narrowing would be busywork. Sticky, never cleared:
        // clearing it on the way back to "all" re-applied the cap and hid every
        // page the user had loaded from the server — 224 rows back down to 15.
        this.expanded = this.expanded || this.filter !== "all"
        this._apply()
    }

    // Typing searches every row already loaded, uncapped while there's a query.
    search() {
        this._apply()
    }

    // "+39 more" shows every loaded row; then "Show less" goes back to the
    // first ten (2026-10-14).
    showAll() {
        this.expanded = !this._collapsible()
        this._apply()
    }

    // The Upcoming group shows three; its own "+N more" shows the rest
    // (contacts/_coming_up). A filter or a search already shows every match.
    showUpcoming() {
        this.upcomingExpanded = !this.upcomingExpanded
        this._apply()
    }

    // Legacy grouped-meeting expander, still used by meeting-group rows.
    toggle(e) {
        const group = e.currentTarget.closest(".contact-history-group")
        if (!group) return

        const dates = group.querySelector("[data-history-dates]")
        if (!dates) return

        const open = dates.hidden
        dates.hidden = !open
        group.classList.toggle("is-open", open)
        e.currentTarget.setAttribute("aria-expanded", open ? "true" : "false")
    }

    _apply() {
        if (!this.hasRowTarget) return

        // Upcoming rows are pinned: they obey the filter but never spend the cap
        // or count toward "Show N more". A contact with five meetings booked this
        // week would otherwise push their own history behind the button.
        const capped = this.rowTargets.filter((row) => !this._pinned(row))
        const matching = capped.filter((row) => this._matches(row))
        const cap = this.expanded || this._query() ? matching.length : VISIBLE_CAP

        let shown = 0
        let upcomingHidden = 0
        let upcomingExtras = 0
        const narrowed = this.filter !== "all" || this._query()
        this.rowTargets.forEach((row) => {
            const matches = this._matches(row)
            let visible = this._pinned(row) ? matches : matches && shown < cap
            if (this._upcomingExtra(row)) upcomingExtras += 1
            if (visible && this._upcomingExtra(row) && !this.upcomingExpanded && !narrowed) {
                visible = false
                upcomingHidden += 1
            }
            row.hidden = !visible
            if (visible && !this._pinned(row)) shown += 1
        })
        if (this.hasUpcomingMoreTarget) {
            const less = this.upcomingExpanded && !narrowed && upcomingExtras > 0
            this.upcomingMoreTarget.hidden = upcomingHidden === 0 && !less
            this.upcomingMoreTarget.textContent = less ? "Show less" : `+${upcomingHidden} ${this.upcomingMoreTarget.dataset.noun || "more"}`
        }

        this._syncMonths()
        this._syncChips()

        const hiddenCount = matching.length - shown
        if (this.hasMoreBtnTarget) {
            const less = hiddenCount <= 0 && this._collapsible()
            this.moreBtnTarget.hidden = hiddenCount <= 0 && !less
            this.moreBtnTarget.textContent = less ? "Show less" : `+${hiddenCount} more`
        }
        // One control at a time: "Show 12 more" is about rows already here, so
        // offering an older page alongside it reads as two ways to do one thing.
        if (this.hasLoadMoreTarget) this.loadMoreTarget.hidden = hiddenCount > 0
        // A filter can legitimately match nothing — say so rather than leaving
        // an empty panel that looks broken.
        if (this.hasEmptyTarget) this.emptyTarget.hidden = matching.length > 0
    }

    _matches(row) {
        // A row can answer to two filters: an email thread with memories filed
        // on it is Email AND Memories ("email memory").
        if (this.filter !== "all" && !this._sources(row).includes(this.filter)) return false

        const query = this._query()
        return !query || row.textContent.toLowerCase().includes(query)
    }

    _query() {
        return this.hasSearchTarget ? this.searchTarget.value.trim().toLowerCase() : ""
    }

    _pinned(row) {
        return row.dataset.activityPinned === "true"
    }

    // Everything is showing only because it was asked for, and folding it
    // back would hide something: "Show less" has a job.
    _collapsible() {
        if (!this.expanded || this.filter !== "all" || this._query()) return false

        return this.rowTargets.filter((row) => !this._pinned(row) && this._matches(row)).length > VISIBLE_CAP
    }

    _upcomingExtra(row) {
        return row.dataset.upcomingExtra === "true"
    }

    // A month heading with every row under it hidden is a heading for nothing.
    _syncMonths() {
        this.monthTargets.forEach((month) => {
            let sibling = month.nextElementSibling
            let anyVisible = false
            while (sibling && !sibling.hasAttribute("data-month-heading")) {
                if (sibling.dataset.activitySource && !sibling.hidden) {
                    anyVisible = true
                    break
                }
                sibling = sibling.nextElementSibling
            }
            month.hidden = !anyVisible
        })
    }

    _syncChips() {
        // Counts describe what's in the stream, and the stream grows: rendered
        // once on the server they read "All 27" over 224 loaded rows.
        const counts = new Map([...this._counts()].map(([source, n]) => [source, this._abbreviate(n)]))
        counts.set("all", this._abbreviate(this.rowTargets.length))
        syncFilterChip(this.hasTriggerTarget ? this.triggerTarget : null, this.chipTargets, this.filter, counts)
    }

    // Rows per kind - except Memories, which counts memories: a thread that
    // carries three counts three (data-memory-count).
    _counts() {
        return this.rowTargets.reduce((acc, row) => {
            this._sources(row).forEach((source) => {
                const n = source === "memory" ? Number(row.dataset.memoryCount || 1) : 1
                acc.set(source, (acc.get(source) || 0) + n)
            })
            return acc
        }, new Map())
    }

    _sources(row) {
        return (row.dataset.activitySource || "").split(/\s+/).filter(Boolean)
    }

    // Mirrors ApplicationHelper#abbreviate_count, which renders the first one.
    // Drifting from it would make the number change shape on the first append.
    _abbreviate(count) {
        if (count < 1000) return count.toLocaleString("en-US")

        const thousands = Math.round((count / 1000) * 10) / 10
        return `${Number.isInteger(thousands) ? thousands : thousands.toFixed(1)}k`
    }
}
